import React from 'react';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ReactMarkdown from 'react-markdown';
import { Badge } from '../Badge/Badge';
import { Image } from '../Image/Image';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import './FeaturedCards.scss';

/**
 * FeaturedCards - Icon card grid block for Poly CMS
 *
 * @deprecated Use `CardGrid` for new content. `FeaturedCards` is kept for
 * backward compatibility with existing PolyC pages and will render
 * indefinitely, but new content should register as `CardGrid` (with
 * `mediaStyle="icon"` for icon cards or the default `mediaStyle="image"`
 * for image-on-top editorial cards).
 *
 * Renders a grid of cards with an icon above a title, description checklist,
 * and optional links. Used for NSI incentive cards, feature highlights, etc.
 *
 * Supports 2 or 3 columns and one background variant. Section header with
 * optional badge, heading, subheading, and ArrowLink CTA.
 */

/** Per-card link fields */
interface CardLinkFields {
  text?: string;
  href?: string;
  external?: boolean;
}

/** Assembled card data (internal) */
interface CardData {
  heading?: string;
  description?: string[];
  imageUrl?: string;
  imageAlt?: string;
  links: CardLinkFields[];
}

/** Per-card flat props for CMS (cards 1-6). Flat + annotatable (was a mapped type). */
export interface CardSlotProps {
  /** Card 1 */
  card1Heading?: string;
  card1Description?: string[];
  /** @polyType Url */
  card1ImageUrl?: string;
  card1ImageAlt?: string;
  card1Link1Text?: string;
  /** @polyType Url */
  card1Link1Href?: string;
  card1Link1External?: boolean;
  card1Link2Text?: string;
  /** @polyType Url */
  card1Link2Href?: string;
  card1Link2External?: boolean;
  card1Link3Text?: string;
  /** @polyType Url */
  card1Link3Href?: string;
  card1Link3External?: boolean;
  /** Card 2 */
  card2Heading?: string;
  card2Description?: string[];
  /** @polyType Url */
  card2ImageUrl?: string;
  card2ImageAlt?: string;
  card2Link1Text?: string;
  /** @polyType Url */
  card2Link1Href?: string;
  card2Link1External?: boolean;
  card2Link2Text?: string;
  /** @polyType Url */
  card2Link2Href?: string;
  card2Link2External?: boolean;
  card2Link3Text?: string;
  /** @polyType Url */
  card2Link3Href?: string;
  card2Link3External?: boolean;
  /** Card 3 */
  card3Heading?: string;
  card3Description?: string[];
  /** @polyType Url */
  card3ImageUrl?: string;
  card3ImageAlt?: string;
  card3Link1Text?: string;
  /** @polyType Url */
  card3Link1Href?: string;
  card3Link1External?: boolean;
  card3Link2Text?: string;
  /** @polyType Url */
  card3Link2Href?: string;
  card3Link2External?: boolean;
  card3Link3Text?: string;
  /** @polyType Url */
  card3Link3Href?: string;
  card3Link3External?: boolean;
  /** Card 4 */
  card4Heading?: string;
  card4Description?: string[];
  /** @polyType Url */
  card4ImageUrl?: string;
  card4ImageAlt?: string;
  card4Link1Text?: string;
  /** @polyType Url */
  card4Link1Href?: string;
  card4Link1External?: boolean;
  card4Link2Text?: string;
  /** @polyType Url */
  card4Link2Href?: string;
  card4Link2External?: boolean;
  card4Link3Text?: string;
  /** @polyType Url */
  card4Link3Href?: string;
  card4Link3External?: boolean;
  /** Card 5 */
  card5Heading?: string;
  card5Description?: string[];
  /** @polyType Url */
  card5ImageUrl?: string;
  card5ImageAlt?: string;
  card5Link1Text?: string;
  /** @polyType Url */
  card5Link1Href?: string;
  card5Link1External?: boolean;
  card5Link2Text?: string;
  /** @polyType Url */
  card5Link2Href?: string;
  card5Link2External?: boolean;
  card5Link3Text?: string;
  /** @polyType Url */
  card5Link3Href?: string;
  card5Link3External?: boolean;
  /** Card 6 */
  card6Heading?: string;
  card6Description?: string[];
  /** @polyType Url */
  card6ImageUrl?: string;
  card6ImageAlt?: string;
  card6Link1Text?: string;
  /** @polyType Url */
  card6Link1Href?: string;
  card6Link1External?: boolean;
  card6Link2Text?: string;
  /** @polyType Url */
  card6Link2Href?: string;
  card6Link2External?: boolean;
  card6Link3Text?: string;
  /** @polyType Url */
  card6Link3Href?: string;
  card6Link3External?: boolean;
}

/**
 * @polyDeprecated true
 * @polySemanticUsage Deprecated: do not select this block for new content; existing pages keep
 * rendering. For a grid of cards, use CardGrid instead.
 */
export interface FeaturedCardsProps extends React.HTMLAttributes<HTMLElement>, CardSlotProps {
  /**
   * Number of columns in the grid layout
   * @polyType Number
   */
  columns?: 2 | 3;

  /**
   * Visual style variant
   * @polyOptions White Shadow=white-shadow
   */
  variant?: 'white-shadow';

  /** Section-level badge text (e.g. "New Seller Incentives") */
  sectionBadgeText?: string;
  /** Section heading */
  sectionHeading?: string;
  /** Section subheading */
  sectionSubheading?: string;
  /** Section link text */
  sectionLinkText?: string;
  /**
   * Section link href
   * @polyType Url
   */
  sectionLinkHref?: string;

  /**
   * CSS max size for card images (e.g. '48px')
   * @polyTranslatable false
   */
  imageMaxHeight?: string;
}

/** Extract card data from flat CMS props for a given slot (1-6) */
function getCard(props: CardSlotProps, slot: 1 | 2 | 3 | 4 | 5 | 6): CardData | null {
  const heading = props[`card${slot}Heading`];
  if (!heading) return null;
  return {
    heading,
    description: props[`card${slot}Description`],
    imageUrl: props[`card${slot}ImageUrl`],
    imageAlt: props[`card${slot}ImageAlt`],
    links: [
      { text: props[`card${slot}Link1Text`], href: props[`card${slot}Link1Href`], external: props[`card${slot}Link1External`] },
      { text: props[`card${slot}Link2Text`], href: props[`card${slot}Link2Href`], external: props[`card${slot}Link2External`] },
      { text: props[`card${slot}Link3Text`], href: props[`card${slot}Link3Href`], external: props[`card${slot}Link3External`] },
    ],
  };
}

/** Pattern to identify card slot props (card1*, card2*, etc.) */
const cardPropPattern = /^card[1-6]/;

/** Render links */
function renderLinks(links: CardLinkFields[]) {
  const valid = links.filter((l): l is { text: string; href: string; external: boolean | undefined } => !!(l.text && l.href));
  if (valid.length === 0) return null;
  return (
    <div className="featured-cards__links">
      <SpaceBetween size="xs">
        {valid.map((link, i) => (
          <ArrowLink
            key={i}
            href={link.href}
            direction={link.external ? 'diagonal' : 'right'}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
          >
            {link.text}
            {link.external && (
              <span className="poly-visually-hidden">(opens in new tab)</span>
            )}
          </ArrowLink>
        ))}
      </SpaceBetween>
    </div>
  );
}

export const FeaturedCards: React.FC<FeaturedCardsProps> = ({
  columns = 3,
  variant = 'white-shadow',
  sectionBadgeText,
  sectionHeading,
  sectionSubheading,
  sectionLinkText,
  sectionLinkHref,
  imageMaxHeight,
  ...rest
}) => {
  // Separate HTML attributes from card slot props
  const {
    className: extraClassName,
    ...mixedProps
  } = rest as React.HTMLAttributes<HTMLElement> & CardSlotProps;

  // Strip card slot props so they don't leak onto the DOM <section> element
  const containerProps = Object.fromEntries(
    Object.entries(mixedProps).filter(([key]) => !cardPropPattern.test(key))
  );

  // Assemble cards from flat props (up to 6)
  const cards: CardData[] = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getCard(rest as CardSlotProps, slot))
    .filter((c): c is CardData => c !== null);

  const colClass = columns === 2 ? ' featured-cards--2col' : ' featured-cards--3col';

  return (
    <section
      {...containerProps}
      className={`featured-cards featured-cards--${variant}${colClass} ${extraClassName || ''}`}
    >
      <div className="featured-cards__container">
        {/* Section header */}
        {(sectionHeading || sectionBadgeText) && (
          <header className="featured-cards__section-header">
            {sectionBadgeText && (
              <Badge size="md" tone="nsi" icon="star" className="featured-cards__badge">
                {sectionBadgeText}
              </Badge>
            )}
            {sectionHeading && <h2 className="featured-cards__section-heading">{sectionHeading}</h2>}
            {sectionSubheading && <p className="featured-cards__section-subheading">{sectionSubheading}</p>}
            {sectionLinkText && sectionLinkHref && (
              <ArrowLink href={sectionLinkHref} className="featured-cards__section-link">
                {sectionLinkText}
              </ArrowLink>
            )}
          </header>
        )}

        {/* Card grid */}
        <div className="featured-cards__grid">
          {cards.map((card, index) => renderIconCard(card, index, imageMaxHeight))}
        </div>
      </div>
    </section>
  );
};

/** Icon card — icon + heading + checklist description + links */
function renderIconCard(card: CardData, index: number, imageMaxHeight?: string) {
  return (
    <article key={`${card.heading}-${index}`} className="featured-cards__card">
      {card.imageUrl && (
        <div
          className="featured-cards__icon"
          aria-hidden="true"
          style={imageMaxHeight ? { maxHeight: imageMaxHeight, maxWidth: imageMaxHeight } : undefined}
        >
          <Image url={card.imageUrl} alt={card.imageAlt ?? card.heading ?? ''} />
        </div>
      )}

      <h3 className="featured-cards__heading">{card.heading}</h3>

      {card.description && card.description.length > 0 && (
        <div className="featured-cards__description">
          {card.description.map((item, i) => (
            <p key={i} className="featured-cards__description-item">
              <CustomIcon name="check" size={20} aria-hidden="true" />
              <ReactMarkdown components={{ p: ({ children }) => <span>{children}</span> }}>{item}</ReactMarkdown>
            </p>
          ))}
        </div>
      )}

      {renderLinks(card.links)}
    </article>
  );
}
